import { useAuth } from "@/src/contexts/auth.context";
import {
  markSessionAlive,
  setActiveOrg,
  setOrgSelected,
  setRememberMe,
  useLoginMutation,
} from "@/src/services/api/auth.api";
import { clearSessionCache } from "@/src/services/blocks/session";
import { resolveLoginError } from "@/src/utils/login-error.utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const loginSchema = z.object({
  email: z.string().min(1, "ERROR_NO_EMAIL").email("ERROR_EMAIL_NOT_VALID"),
  password: z.string().min(1, "ERROR_NO_PASSWORD"),
  rememberMe: z.boolean().default(true),
});

export type LoginFormData = Readonly<z.input<typeof loginSchema>>;

export type SSOStatus = Readonly<{
  message: string;
  type: "success" | "error";
}>;

export const useLogin = () => {
  const router = useRouter();
  const { t } = useTranslation();
  const params = useLocalSearchParams();
  const { code, state } = params;

  const [ssoStatus, setSsoStatus] = useState<SSOStatus | null>(null);

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
      // No UI toggle for this anymore — every session is remembered.
      rememberMe: true,
    },
  });

  const { refreshAuth } = useAuth();

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError: setFormError,
    clearErrors,
  } = form;

  /**
   * A provider can still land on `/login?code&state` (an older registered
   * redirect, or a user re-opening the link). The v4 code exchange lives in
   * exactly one place — `app/auth/callback.tsx` via `finishLogin` — so this
   * forwards there instead of exchanging here.
   *
   * What used to be here was `useSSOExchangeMutation`: the v1
   * `grant_type=social` password grant against `/idp/v1/Authentication/Token`,
   * which now 404s `Application_Not_Found`. It also stored the returned token
   * pair in JS, which the cookie-session model has no use for.
   */
  const [isExchanging, setIsExchanging] = useState(false);

  const loginMutation = useLoginMutation({
    onSuccess: async () => {
      // The HttpOnly session cookie is the session; the body tokens are ignored
      // on purpose (see useLoginMutation). Drop any cached identity/org from a
      // previous session so AuthProvider reads the new one.
      clearSessionCache();
      // The session now belongs to this run; the Remember me check at the next
      // cold start reads this marker (see `discardUnrememberedSession`).
      markSessionAlive();
      await setActiveOrg(undefined);
      await setOrgSelected(false);
      await refreshAuth();
      router.replace("/");
    },
    onError: (err: unknown) => {
      // Every branch of this mapping lives in `resolveLoginError` so it can be
      // tested directly — see `login-error.utils`. `t()` passes an unknown key
      // through unchanged, so a server-supplied message renders as-is.
      const { field, message } = resolveLoginError(err);
      setFormError(field, { message: t(message) });
    },
  });

  const onLogin = async (data: LoginFormData) => {
    setSsoStatus(null);
    // Awaited: `onSuccess` marks the run alive and the startup check reads the
    // preference, so it has to be on disk before either can run.
    await setRememberMe(data.rememberMe ?? true);
    loginMutation.mutate({
      email: data.email,
      password: data.password,
      rememberMe: data.rememberMe,
    });
  };

  const handleSSOError = (error: string, status?: string) => {
    setSsoStatus({
      message: error,
      type: "error",
    });
    clearErrors("email");
  };

  useEffect(() => {
    if (!code || !state) return;
    setIsExchanging(true);
    router.replace({
      pathname: "/auth/callback",
      params: { code: code as string, state: state as string },
    });
  }, [code, state, router]);

  /**
   * The live toggle value, for the SSO buttons — they start a redirect that
   * leaves this screen, so they have to persist the preference themselves before
   * handing off. Remember me applies to every way in, not just the password one.
   */
  const rememberMe = useWatch({ control, name: "rememberMe" }) ?? true;

  const navigateToForgotPassword = () => {
    router.push("/forgot-password");
  };


  return {
    control,
    handleSubmit,
    errors,

    ssoStatus,
    rememberMe,
    isAuthenticating: isExchanging,
    isLoggingIn: loginMutation.isPending,

    onLogin,
    handleSSOError,
    navigateToForgotPassword,

    t,
  };
};
